import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import HubNavBar from '../components/HubNavBar';
import { Mail, RefreshCw, Trash2, ChevronDown, ChevronUp, Package, FileText, AlertCircle, Clock, CheckCircle2, Loader2, Inbox, UserCog } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const enconOrange = '#ee7203';

const TYPE_CONFIG = {
  order:                     { label: 'ORDER',        bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  quote:                     { label: 'QUOTE',        bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  purchase_order:            { label: 'PURCHASE',     bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200'  },
  purchase_order_direct:     { label: 'DIRECT',       bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-200'    },
  direct_ack:                { label: 'DIRECT ACK',   bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-200'   },
  daily_order_entry_report:  { label: 'RTOB',         bg: 'bg-teal-100',   text: 'text-teal-700',   border: 'border-teal-200'   },
  order_book_summary_report: { label: 'ORDER BOOK',   bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-200'  },
  free_stock_report:         { label: 'FREE STOCK',   bg: 'bg-sky-100',    text: 'text-sky-700',    border: 'border-sky-200'    },
  unrecognized_report:       { label: 'UNRECOGNISED', bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-200'    },
  credit_reinvoice:          { label: 'CREDIT/RE-INV', bg: 'bg-slate-100', text: 'text-slate-600',  border: 'border-slate-200'  },
  unknown:                   { label: '?',            bg: 'bg-gray-100',   text: 'text-gray-500',   border: 'border-gray-200'   },
};

const STATUS_CONFIG = {
  received: { icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />, label: 'Parsing…',  color: 'text-blue-500'  },
  parsed:   { icon: <CheckCircle2 className="w-3.5 h-3.5" />,         label: 'Parsed',    color: 'text-green-600' },
  failed:   { icon: <AlertCircle className="w-3.5 h-3.5" />,          label: 'Failed',    color: 'text-red-500'   },
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function EmailRow({ email, onDelete, isSuperAdmin, assignableUsers, onReassign }) {
  const [expanded, setExpanded] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const tc = TYPE_CONFIG[email.email_type] || TYPE_CONFIG.unknown;
  const sc = STATUS_CONFIG[email.status] || STATUS_CONFIG.received;
  const hasLines = (email.product_lines || []).length > 0;
  const canReassign = isSuperAdmin && ['quote', 'order'].includes(email.email_type);

  const handleReassignChange = async (value) => {
    setReassigning(true);
    try {
      await onReassign(email.id, value === '__unassign__' ? null : value);
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div className={`rounded-xl border bg-white mb-2 overflow-hidden transition-shadow hover:shadow-sm ${tc.border}`}>
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Type badge */}
        <span className={`text-[10px] font-bold px-2 py-1 rounded-lg flex-shrink-0 ${tc.bg} ${tc.text}`}>
          {tc.label}
        </span>

        {/* Customer + order */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-900 truncate">
              {email.customer_name || <span className="text-gray-400 font-normal">Unknown customer</span>}
            </span>
            {email.order_number && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-mono">
                {email.order_number}
              </span>
            )}
            {email.reference && (
              <span className="text-xs text-gray-400">Ref: {email.reference}</span>
            )}
            {email.assigned_to_name && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700" data-testid={`reassigned-badge-${email.id}`}>
                Reassigned to {email.assigned_to_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-400 truncate">{email.from_email}</span>
            <span className="text-gray-200 text-xs">·</span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />{timeAgo(email.received_at)}
            </span>
            <span className="text-gray-200 text-xs">·</span>
            <span className="text-xs text-gray-400 truncate">{email.filename}</span>
          </div>
        </div>

        {/* Reassign (super admin only, quotes/orders only) */}
        {canReassign && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Select
              value={email.assigned_to_user_id || '__unassign__'}
              onValueChange={handleReassignChange}
              disabled={reassigning}
            >
              <SelectTrigger
                className="h-7 w-36 text-xs border-purple-200 text-purple-700"
                data-testid={`reassign-select-${email.id}`}
              >
                {reassigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCog className="w-3 h-3 mr-1 flex-shrink-0" />}
                <SelectValue placeholder="Reassign to…" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="__unassign__">— Original sender —</SelectItem>
                {assignableUsers.map(u => (
                  <SelectItem key={u.id} value={u.id} data-testid={`reassign-option-${email.id}-${u.id}`}>
                    {u.full_name || u.username || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Status */}
        <div className={`flex items-center gap-1 text-xs font-medium flex-shrink-0 ${sc.color}`}>
          {sc.icon}
          <span>{sc.label}</span>
        </div>

        {/* Product lines indicator */}
        {hasLines && (
          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full flex-shrink-0">
            {email.product_lines.length} line{email.product_lines.length !== 1 ? 's' : ''}
          </span>
        )}

        {/* Expand */}
        {(hasLines || email.contact_email || email.parse_error) && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}

        {/* Delete */}
        <button
          onClick={() => onDelete(email.id)}
          className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-300 flex-shrink-0 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
          {email.contact_email && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Email</p>
              <p className="text-sm text-gray-700">{email.contact_email}</p>
            </div>
          )}
          {email.parse_error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{email.parse_error}</p>
            </div>
          )}
          {hasLines && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Product Lines ({email.product_lines.length})
              </p>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100 text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-2 text-left font-semibold">Code</th>
                      <th className="px-3 py-2 text-left font-semibold">Description</th>
                      <th className="px-3 py-2 text-right font-semibold">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {email.product_lines.map((line, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-gray-700">{line.product_code || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{line.description || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-700 font-medium">{line.qty ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AutomationsDashboard({ user }) {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [assignableUsers, setAssignableUsers] = useState([]);
  const isSuperAdmin = user?.role === 'super_admin';

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await api.get(`${API}/automations/emails?limit=50`);
      setEmails(res.data?.emails || []);
    } catch {
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get(`${API}/users`)
      .then(res => setAssignableUsers((res.data || []).filter(u => u.approved !== false)))
      .catch(() => setAssignableUsers([]));
  }, [isSuperAdmin]);

  const handleReassign = async (emailId, targetUserId) => {
    try {
      const res = await api.put(`${API}/automations/emails/${emailId}/reassign`, { user_id: targetUserId });
      setEmails(prev => prev.map(e => e.id === emailId ? {
        ...e,
        assigned_to_user_id: targetUserId || undefined,
        assigned_to_name: targetUserId ? res.data?.assigned_to_name : undefined,
      } : e));
      toast.success(targetUserId ? `Reassigned to ${res.data?.assigned_to_name}` : 'Reverted to original sender');
    } catch {
      toast.error('Failed to reassign');
    }
  };

  const handleReparse = async () => {
    if (!window.confirm('Re-parse all stored orders using the updated prompt? This will fix address parsing on all orders that have stored extracted text.')) return;
    setReparsing(true);
    try {
      const res = await api.post(`${API}/automations/reparse-all`);
      const { reparsed, skipped, errors, total, message } = res.data;
      if (message) {
        toast.info(message);
      } else {
        toast.success(`Re-parsed ${reparsed}/${total} orders. Skipped: ${skipped}, Errors: ${errors}`);
      }
      await load(true);
    } catch {
      toast.error('Re-parse failed');
    } finally {
      setReparsing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this email record?')) return;
    try {
      await api.delete(`${API}/automations/emails/${id}`);
      setEmails(prev => prev.filter(e => e.id !== id));
      toast.success('Removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = emails.filter(e => {
    if (activeTab === 'orders')     return e.email_type === 'order';
    if (activeTab === 'quotes')     return e.email_type === 'quote';
    if (activeTab === 'direct_ack') return e.email_type === 'direct_ack';
    return true;
  });

  const counts = {
    all:        emails.length,
    orders:     emails.filter(e => e.email_type === 'order').length,
    quotes:     emails.filter(e => e.email_type === 'quote').length,
    direct_ack: emails.filter(e => e.email_type === 'direct_ack').length,
  };

  const TAB_DEFS = [
    { key: 'all',        label: 'All',        icon: <Inbox className="w-3.5 h-3.5" />    },
    { key: 'orders',     label: 'Orders',     icon: <Package className="w-3.5 h-3.5" />  },
    { key: 'quotes',     label: 'Quotes',     icon: <FileText className="w-3.5 h-3.5" /> },
    { key: 'direct_ack', label: 'Direct ACK', icon: <FileText className="w-3.5 h-3.5" style={{ color: '#2563eb' }} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top nav */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: enconOrange }}>
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Automations</h1>
                <p className="text-xs text-gray-400">Incoming emails · 10-day rolling window</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => load(true)}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {user?.role === 'super_admin' && (
                <>
                  <button
                    onClick={handleReparse}
                    disabled={reparsing}
                    title="Re-parse all stored orders with the updated address prompt"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors"
                    style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
                    data-testid="reparse-all-btn"
                  >
                    {reparsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {reparsing ? 'Re-parsing…' : 'Re-parse All'}
                  </button>
                  <button
                    onClick={() => navigate('/settings?tab=cloudflare-incoming')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white transition-colors"
                    style={{ backgroundColor: enconOrange }}
                  >
                    Setup
                  </button>
                </>
              )}
            </div>
          </div>
          <HubNavBar user={user} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-5 bg-white rounded-xl border border-gray-200 p-1 w-fit">
          {TAB_DEFS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              style={activeTab === tab.key ? { backgroundColor: enconOrange } : {}}
            >
              {tab.icon}
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.key ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: enconOrange }} />
            <p className="text-sm">Loading emails…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-400">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <Mail className="w-8 h-8 text-gray-300" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-500 mb-1">No emails yet</p>
              <p className="text-sm text-gray-400">
                {emails.length === 0
                  ? 'Once Cloudflare Email Routing is configured, incoming PDFs will appear here automatically.'
                  : `No ${activeTab} emails in the last 10 days.`}
              </p>
            </div>
            {emails.length === 0 && user?.role === 'super_admin' && (
              <button
                onClick={() => navigate('/settings?tab=cloudflare-incoming')}
                className="mt-2 px-4 py-2 rounded-xl text-sm text-white font-medium"
                style={{ backgroundColor: enconOrange }}
              >
                View Setup Guide
              </button>
            )}
          </div>
        ) : (
          <div>
            <p className="text-xs text-gray-400 mb-3">
              Showing {filtered.length} email{filtered.length !== 1 ? 's' : ''} · auto-deleted after 10 days
            </p>
            {filtered.map(email => (
              <EmailRow
                key={email.id}
                email={email}
                onDelete={handleDelete}
                isSuperAdmin={isSuperAdmin}
                assignableUsers={assignableUsers}
                onReassign={handleReassign}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
